import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import type { DemandSummaryMonth } from "@/lib/data/requests/reports/reports-demand-summary";

const monthLabel = (value: string) =>
  new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    month: "short",
  });
const tooltipMonthLabel = (value: string) =>
  new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

export function MonthlyDemandChart({
  months,
}: {
  months: DemandSummaryMonth[];
}) {
  const width = 1100;
  const height = 320;
  const left = 56;
  const top = 32;
  const chartHeight = 240;
  const maximum = Math.max(1, ...months.map((month) => month.count));
  const ceiling = Math.ceil(maximum / 4) * 4;
  const step = (width - left - 20) / Math.max(1, months.length);
  return (
    <TooltipProvider delayDuration={100}>
      <figure className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full min-w-160"
          role="group"
          aria-label="Total demands sent by month"
        >
          <title>Total demands sent by month</title>
          <desc>
            {months
              .map((month) => `${monthLabel(month.month)}: ${month.count}`)
              .join("; ")}
          </desc>
          {[0, 1, 2, 3, 4].map((index) => {
            const y = top + chartHeight - (index / 4) * chartHeight;
            return (
              <g key={index}>
                <line
                  x1={left}
                  x2={width - 20}
                  y1={y}
                  y2={y}
                  className="stroke-border"
                />
                <text
                  x={left - 10}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-muted-foreground text-xs"
                >
                  {((ceiling * index) / 4).toLocaleString()}
                </text>
              </g>
            );
          })}
          {months.map((month, index) => {
            const barHeight = (month.count / ceiling) * chartHeight;
            const x = left + step * index + step * 0.2;
            const y = top + chartHeight - barHeight;
            return (
              <Tooltip key={month.month}>
                <TooltipTrigger asChild>
                  <g
                    tabIndex={0}
                    role="img"
                    aria-label={`${tooltipMonthLabel(month.month)}: ${month.count.toLocaleString()} demands sent`}
                    className="group outline-none"
                  >
                    <rect
                      x={left + step * index}
                      y={top}
                      width={step}
                      height={chartHeight + 42}
                      fill="transparent"
                    />
                    <rect
                      x={x}
                      y={y}
                      width={step * 0.6}
                      height={barHeight}
                      className="fill-primary group-hover:opacity-80 group-focus:opacity-80"
                      rx={2}
                    />
                    <text
                      x={x + step * 0.3}
                      y={y - 8}
                      textAnchor="middle"
                      className="fill-foreground text-xs font-medium"
                    >
                      {month.count.toLocaleString()}
                    </text>
                    <text
                      x={x + step * 0.3}
                      y={top + chartHeight + 26}
                      textAnchor="middle"
                      className="fill-muted-foreground text-xs"
                    >
                      {monthLabel(month.month)}
                    </text>
                  </g>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent
                    side="left"
                    align="center"
                    sideOffset={8}
                    className="max-h-72 min-w-52 overflow-y-auto p-3"
                  >
                    <div className="mb-2 font-semibold">
                      {tooltipMonthLabel(month.month)}
                    </div>
                    <dl className="space-y-1.5">
                      {(month.teams ?? []).map((team) => (
                        <div
                          key={team.teamId ?? "unassigned"}
                          className="flex justify-between gap-6"
                        >
                          <dt>{team.label}</dt>
                          <dd className="tabular-nums">
                            {team.count.toLocaleString()}
                          </dd>
                        </div>
                      ))}
                      {!month.teams && <div>Team counts unavailable</div>}
                      <div className="flex justify-between gap-6 border-t pt-2 font-semibold">
                        <dt>Total</dt>
                        <dd className="tabular-nums">
                          {month.count.toLocaleString()}
                        </dd>
                      </div>
                    </dl>
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            );
          })}
        </svg>
      </figure>
    </TooltipProvider>
  );
}
